import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import {AVAILABLE_PAGE_SIZES} from 'nav/sidepanel/PageSizeUtil';
import {FilterOption} from 'search/filters/base/FilterComponents';
import ListingFiltersContainer from 'search/filters/ListingFiltersContainer';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {FilterKey, Filter, Operator, SearchType, SortKey} from 'search/SearchConstants';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import Link from 'ui-library/Link';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import {CHECKBOX_COLUMN_WIDTH} from 'ui-library/RichObjectTable';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';
import {InputChangeEvent} from 'util/Types';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionFileStatus, getConnectionFileStatus, fileStatusDisplay} from './ConnectionFilesStatus';

const styles = StyleSheet.create({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  table: {
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    marginBottom: 0,
    marginRight: 0,
    marginLeft: 0,
  },
  tableRow: {
    height: '36px',
  },
  tableCell: {
    padding: '0 13px',
  },
  stickyCheckboxCell: {
    textAlign: 'center',
    padding: 0,
    minWidth: CHECKBOX_COLUMN_WIDTH,
    maxWidth: CHECKBOX_COLUMN_WIDTH,
    width: CHECKBOX_COLUMN_WIDTH,
    marginTop: '16px',
    position: 'sticky',
    left: 0,
    backgroundColor: Colors.WHITE,
    zIndex: 2,
    boxShadow: `inset 0 1px 0 0 ${Colors.GRAY_LIGHT}`,
  },
  stickyCheckboxHeaderCell: {
    textAlign: 'center',
    padding: 0,
    minWidth: CHECKBOX_COLUMN_WIDTH,
    maxWidth: CHECKBOX_COLUMN_WIDTH,
    width: CHECKBOX_COLUMN_WIDTH,
    position: 'sticky',
    left: 0,
    backgroundColor: Colors.GRAY_LIGHTER,
    zIndex: 3,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  stickyCell: {
    padding: '0 13px',
    position: 'sticky',
    left: CHECKBOX_COLUMN_WIDTH,
    backgroundColor: Colors.WHITE,
    zIndex: 1,
    boxShadow: `inset 0 1px 0 0 ${Colors.GRAY_LIGHT}`,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -4,
      bottom: 0,
      width: 4,
      background: `linear-gradient(to right, ${Colors.GRAY_LIGHT}, transparent)`,
      pointerEvents: 'none',
    },
  },
  stickyHeaderCell: {
    padding: '0 13px',
    position: 'sticky',
    left: CHECKBOX_COLUMN_WIDTH,
    backgroundColor: Colors.GRAY_LIGHTER,
    zIndex: 2,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -4,
      bottom: 0,
      width: 4,
      background: `linear-gradient(to right, ${Colors.GRAY_LIGHT}, transparent)`,
      pointerEvents: 'none',
    },
  },
  stickyCellNoCheckbox: {
    padding: '0 13px',
    position: 'sticky',
    left: 0,
    backgroundColor: Colors.WHITE,
    zIndex: 1,
    boxShadow: `inset 0 1px 0 0 ${Colors.GRAY_LIGHT}`,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -4,
      bottom: 0,
      width: 4,
      background: `linear-gradient(to right, ${Colors.GRAY_LIGHT}, transparent)`,
      pointerEvents: 'none',
    },
  },
  stickyHeaderCellNoCheckbox: {
    padding: '0 13px',
    position: 'sticky',
    left: 0,
    backgroundColor: Colors.GRAY_LIGHTER,
    zIndex: 2,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -4,
      bottom: 0,
      width: 4,
      background: `linear-gradient(to right, ${Colors.GRAY_LIGHT}, transparent)`,
      pointerEvents: 'none',
    },
  },
  filePathCell: {
    padding: '0 13px',
    whiteSpace: 'nowrap',
  },
  checkbox: {
    accentColor: Colors.BLUE,
  },
  checkboxCell: {
    textAlign: 'center',
    padding: 0,
    width: '20px',
    marginTop: '16px',
  },
  searchFilter: {
    width: '350px',
    marginRight: '10px',
  },
});
function ConnectionFilesTableCell(props: {
  className?: string;
  children: JSX.Element;
  tooltipText?: JSX.Element | undefined;
  onSelectRow: () => void;
}): JSX.Element {
  return (
    <Cell className={props.className} onClick={props.onSelectRow}>
      <Tooltip isDisabled={props.tooltipText == null} text={props.tooltipText}>
        {props.children}
      </Tooltip>
    </Cell>
  );
}

ConnectionFilesTableCell.defaultProps = {
  className: css(styles.tableCell),
};

const DEFAULT_PAGE_SIZE = 10;

const COLUMN_HEADER_TO_CELL: {
  [columnHeader: string]: (
    connectionFile: FileListingGraphQL,
    props: ConnectionFilesTableProps,
    onRefetchComplete: () => Promise<void>
  ) => JSX.Element | string | undefined;
} = {
  'File name': (connectionFile: FileListingGraphQL, props: ConnectionFilesTableProps) => {
    const filename = connectionFile.name || connectionFile.scratchFile.filename;
    if (props.onSelectRows && props.hideCheckboxColumn) {
      return <a>{filename}</a>;
    }
    return filename;
  },
  'Modified at': (connectionFile: FileListingGraphQL) => DateUtil.formalTime(connectionFile.modifiedAt),
  'Created at': (connectionFile: FileListingGraphQL) => DateUtil.formalTime(connectionFile.createdAt),
  Status: (connectionFile: FileListingGraphQL) => fileStatusDisplay(connectionFile, true),
  'File path': (connectionFile: FileListingGraphQL) => {
    const filename = connectionFile.scratchFile.filename;
    const lastSlash = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
    return lastSlash >= 0 ? filename.substring(0, lastSlash + 1) : '';
  },
};

/**
 * If it exists, extract the list of connectionIds that are being used in filters's lineage parent filter.
 */
export function getConnectionIdsFromFilters(filters: Array<Filter>): Array<string> {
  return (
    (filters.find((filter_) => filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER)
      ?.value as Array<string>) ?? []
  );
}

/**
 * Get the base filters to apply to ConnectionFilesTable, given a list of connectionIds.
 */
export function getFiltersFromConnectionIds(connectionIds: Array<string>): Array<Filter> {
  const filters: Array<Filter> = [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [ModelTypeConstants.FILE],
    },
    {
      key: FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
      operator: Operator.IS_ONE_OF,
      value: connectionIds,
    },
    {
      key: FilterKey.ARCHIVE_PURPOSES,
      operator: Operator.IS_ONE_OF,
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];
  return filters;
}

type InitialConnectionIdsState =
  | {
      initialConnectionIds: Array<string>;
      initialFilters: null;
    }
  | {
      initialFilters: Array<Filter>;
      initialConnectionIds: null;
    };

interface UseConnectionIdsState {
  connectionIdsState: [Array<string>, (newConnectionIds: Array<string>) => void];
  filtersState: [Array<Filter>, (newFilters: Array<Filter>) => void];
}

/**
 * Use connected connectionIds and filters state.
 *
 * Each setter that is returned will update both the connectionIds and the filters state when called.
 *
 * @param state Initial state, as defined by _either_ initialConnectionIds or initialFilters. Whichever state
 *              is defined will be translated in order to create the other additional state.
 */
export function useConnectionIdsAndFiltersState(state: InitialConnectionIdsState): UseConnectionIdsState {
  const [connectionIds, _setConnectionIds] = useState<Array<string>>(
    state.initialConnectionIds ?? getConnectionIdsFromFilters(state.initialFilters)
  );
  const [filters, _setFilters] = useState<Array<Filter>>(
    state.initialFilters ?? getFiltersFromConnectionIds(state.initialConnectionIds)
  );

  // When connectionIds is set, then also add or update the lineage parent filter in filters
  const setConnectionIds = useCallback(
    (newConnectionIds: Array<string>) => {
      _setConnectionIds(newConnectionIds);
      _setFilters((prevFilters) => {
        const hasConnectionFilter = prevFilters.some(
          (filter_) => filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
        );
        if (hasConnectionFilter) {
          // If the connection filter already exists, then overwrite its value with connectionIds
          return prevFilters.map((filter_) =>
            filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
              ? {...filter_, value: newConnectionIds}
              : filter_
          );
        } else {
          // Otherwise create the connection filter and append it to the end of filters
          return [
            ...prevFilters,
            {
              key: FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
              operator: Operator.IS_ONE_OF,
              value: newConnectionIds,
            },
          ];
        }
      });
    },
    [_setConnectionIds, _setFilters]
  );

  // When filters is set, then also update connectionIds to reflect the lineage parent filter in filters. This
  // raises if a lineage parent filter is not present because a missing lineage parent filter has the effect
  // of displaying _all_ accessible data catalog files in the ConnectionFilesTable, for which there is no
  // meaningful translation from filters to connectionIds.
  const setFilters = useCallback(
    (newFilters: Array<Filter>) => {
      _setFilters(newFilters);

      const hasConnectionFilter = newFilters.some(
        (filter_) => filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
      );
      assert(hasConnectionFilter, 'Filters is unexpectedly missing a lineage parent filter.');

      const newConnectionIds = newFilters.find(
        (filter_) => filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
      )!.value as Array<string>;
      _setConnectionIds(newConnectionIds);
    },
    [_setConnectionIds, _setFilters]
  );

  return {
    connectionIdsState: [connectionIds, setConnectionIds],
    filtersState: [filters, setFilters],
  };
}

export interface ConnectionFilesTableProps {
  isSelectAllDisabled?: boolean;
  // If defined, should be the same length as connectionFiles
  isDisabledMap?: Array<boolean>;
  connectionFiles: Array<FileListingGraphQL>;
  setConnectionFiles: (connectionFiles: Array<FileListingGraphQL>) => void;
  selectedRowIndices?: Set<number>;
  onSelectRows?: (rowIndices: Set<number>) => void;
  allowMultiple?: boolean;
  columns: Array<keyof typeof COLUMN_HEADER_TO_CELL>;
  filters: Array<Filter>;
  setFilters: (newFilters: Array<Filter>) => void;
  hideCheckboxColumn?: boolean;
  children?: React.ReactNode;
  showLineageParentFilter?: boolean;
}

export default function ConnectionFilesTable(props: ConnectionFilesTableProps): JSX.Element {
  const {selectedRowIndices, onSelectRows, setConnectionFiles} = props;
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [numTotalFiles, setNumTotalFiles] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const filterOptions: Array<FilterOption> = [
    {filterKey: FilterKey.CREATED_AT},
    {filterKey: FilterKey.MODIFIED_AT},
    {filterKey: FilterKey.STARRED},
  ];
  if (props.showLineageParentFilter) {
    filterOptions.push({filterKey: FilterKey.LINEAGE_PARENT_API_IDENTIFIER});
  }

  // Inject/overwrite the model type filter into the filters defined by the parent, since this component
  // should only ever search for FILE type models, regardless of what model type the caller tries to filter by.
  const allFilters: Array<Filter> = useMemo(
    () => [
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: [ModelTypeConstants.FILE],
      } as Filter,
      ...props.filters.filter((filter_) => filter_.key !== FilterKey.TYPES),
    ],
    [props.filters]
  );
  const fetchFiles = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    const response = (await GlobalSearchActions.doGlobalSearch(
      {
        offset,
        limit: pageSize,
        sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
        filters: allFilters,
        query: searchFilter,
      },
      SearchType.GLOBAL,
      'instruments:CONNECTION_FILE_LISTING'
    )) as GlobalSearchResponse;

    setConnectionFiles(Object.values(response.filesById));
    setIsLoading(false);
    setNumTotalFiles(response.total);
  }, [allFilters, offset, searchFilter, setConnectionFiles, pageSize]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return (
    <div>
      <div className="u-flex-display u-spacing-bottom">
        <PlainSearchInput
          className={css(styles.searchFilter)}
          clearType={ClearType.X}
          onEscape={() => {
            setSearchFilter('');
          }}
          onChange={(e: InputChangeEvent) => {
            setOffset(0);
            setSearchFilter(e.target.value);
          }}
          placeholder="Search"
          value={searchFilter}
        />
        <div className="u-flex-display u-align-items-center">
          <ListingFiltersContainer
            filters={allFilters}
            primaryFilterOptions={[
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {archivePurposes: BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES},
              },
            ]}
            secondaryFilterProps={{
              filterGroups: [
                {
                  name: 'General',
                  filterOptions,
                },
              ],
              isDropdownOpen,
              onSetDropdownOpen: setIsDropdownOpen,
            }}
            selectedSchema={null}
            onChangeFilters={(newFilters: Array<Filter>) => {
              setOffset(0);
              props.setFilters(newFilters);
            }}
          />
        </div>
      </div>
      <div className="u-flex-display u-align-items-center">
        <ResultsPager
          isLoading={isLoading}
          setOffset={setOffset}
          pagination={{
            offset,
            limit: pageSize,
            total: numTotalFiles,
            isExact: true,
          }}
        />
        <ListingSettingsDropdown
          pageSizeConfig={{
            availablePageSizes: AVAILABLE_PAGE_SIZES,
            pageSize,
            onSetPageSize: (newPageSize: number) => {
              setOffset(0);
              setPageSize(newPageSize);
            },
          }}
        />
      </div>
      {isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={css(styles.tableContainer)}>
            <Table
              emptyTableText="No files found"
              numRows={props.connectionFiles.length}
              overflowVisible={true}
              className={css(styles.table)}
              getRowClassName={() => css(styles.tableRow)}
            >
              {selectedRowIndices != null && onSelectRows != null && !props.hideCheckboxColumn ? (
                <Column
                  header={
                    <Cell className={css(styles.stickyCheckboxHeaderCell)}>
                      <IndeterminateCheckbox
                        disabled={!props.allowMultiple || props.isSelectAllDisabled}
                        items={[...Array(props.connectionFiles.length).keys()]}
                        selectedMap={(idx: number): boolean => selectedRowIndices.has(idx)}
                        onChange={() => {
                          const indicesToToggle = new Set(
                            [...Array(props.connectionFiles.length).keys()].filter(
                              (i) => selectedRowIndices.size === 0 || selectedRowIndices.has(i)
                            )
                          );
                          onSelectRows(indicesToToggle);
                        }}
                      />
                    </Cell>
                  }
                  renderCell={(rowIndex: number) => (
                    <ConnectionFilesTableCell
                      className={css(styles.stickyCheckboxCell)}
                      onSelectRow={() => {
                        if (props.isDisabledMap == null || !props.isDisabledMap[rowIndex]) {
                          onSelectRows(new Set([rowIndex]));
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className={css(styles.checkbox)}
                        disabled={
                          (props.isDisabledMap != null && props.isDisabledMap[rowIndex]) ||
                          getConnectionFileStatus(props.connectionFiles[rowIndex], true) !==
                            ConnectionFileStatus.UPLOADED ||
                          (!props.allowMultiple &&
                            selectedRowIndices.size > 0 &&
                            !selectedRowIndices.has(rowIndex))
                        }
                        checked={selectedRowIndices.has(rowIndex)}
                        onChange={
                          noop /* Required to avoid React warning, even though we are controlling the change at the cell level */
                        }
                      />
                    </ConnectionFilesTableCell>
                  )}
                />
              ) : null}
              {props.columns.map((header, i) => {
                const getTooltipText = (rowIndex: number): JSX.Element | undefined => {
                  if (header !== 'Status') {
                    return undefined;
                  }
                  const errorMessage = props.connectionFiles[rowIndex].scratchFile.errorMessage;
                  if (!errorMessage) {
                    return undefined;
                  }

                  return (
                    <>
                      {errorMessage}
                      {errorMessage.slice(-1) !== '.' && '.'}
                      <br />
                      Please visit our{' '}
                      <Link
                        href="https://help.benchling.com/hc/en-us"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        help center
                      </Link>
                      {
                        ' to find our adapter configuration guides or contact Benchling Support for additional help.'
                      }
                    </>
                  );
                };

                const hasCheckboxColumn =
                  selectedRowIndices != null && onSelectRows != null && !props.hideCheckboxColumn;
                const cellClassName =
                  header === 'File name'
                    ? css(hasCheckboxColumn ? styles.stickyCell : styles.stickyCellNoCheckbox)
                    : header === 'File path'
                      ? css(styles.filePathCell)
                      : undefined;
                const headerCell =
                  header === 'File name' ? (
                    <Cell
                      className={css(
                        hasCheckboxColumn ? styles.stickyHeaderCell : styles.stickyHeaderCellNoCheckbox
                      )}
                    >
                      {header}
                    </Cell>
                  ) : (
                    header
                  );

                return (
                  <Column
                    key={i}
                    header={headerCell}
                    renderCell={(rowIndex: number) => (
                      <ConnectionFilesTableCell
                        className={cellClassName}
                        onSelectRow={
                          onSelectRows
                            ? () => {
                                if (props.isDisabledMap == null || !props.isDisabledMap[rowIndex]) {
                                  onSelectRows(new Set([rowIndex]));
                                }
                              }
                            : noop
                        }
                        tooltipText={getTooltipText(rowIndex)}
                      >
                        <>
                          {COLUMN_HEADER_TO_CELL[header](props.connectionFiles[rowIndex], props, fetchFiles)}
                        </>
                      </ConnectionFilesTableCell>
                    )}
                  />
                );
              })}
            </Table>
          </div>
          {props.children}
        </>
      )}
    </div>
  );
}
